/**
 * Web Logger with Sentry integration for Next.js applications
 *
 * ARCHITECTURE (Sentry SDK 8.x+):
 * - Sentry.captureException → Creates Issues (bugs to track and fix)
 * - Sentry.addBreadcrumb → Context trail for debugging crashes
 * - Console logging in development for immediate feedback
 *
 * DEBUGGING TIPS: To reduce log noise during development:
 *
 * 1. Set DEBUG environment variable:
 *    DEBUG=false (disables debug/trace logs in production)
 *
 * Core logging levels (info, warn, error, fatal) are always enabled for safety.
 */

import * as Sentry from "@sentry/nextjs";

import type { ErrorCategory } from "./constants";
import {
  BREADCRUMB_RATE_LIMIT,
  categorizeError,
  isFilteredErrorMessage,
  isFilteredErrorName,
} from "./constants";

type LogLevel = "trace" | "debug" | "info" | "warn" | "error" | "fatal";

export type LogContext = unknown;

/**
 * Statistics about error filtering for monitoring.
 * Returned by logger.getFilteringStats().
 */
export interface FilteringStats {
  /** Number of errors filtered from Sentry */
  filtered: number;
  /** Number of errors sent to Sentry */
  sent: number;
  /** Percentage of errors filtered (e.g., "89%") */
  ratio: string;
}

// Re-export constants for consumers
export { FILTERED_ERROR_PATTERNS, FILTERED_ERROR_NAMES } from "./constants";
export type { ErrorCategory } from "./constants";

/**
 * Rate limiter for breadcrumbs to prevent flooding Sentry
 * with repeated filtered errors.
 */
class BreadcrumbRateLimiter {
  private timestamps: number[] = [];
  private readonly maxCount = BREADCRUMB_RATE_LIMIT.maxCount;
  private readonly windowMs = BREADCRUMB_RATE_LIMIT.windowMs;

  /**
   * Check if we can add a breadcrumb within rate limits.
   * @returns true if breadcrumb can be added, false if rate limited
   */
  canAdd(): boolean {
    const now = Date.now();
    // Remove timestamps outside the window
    this.timestamps = this.timestamps.filter((t) => now - t < this.windowMs);

    if (this.timestamps.length >= this.maxCount) {
      return false;
    }

    this.timestamps.push(now);
    return true;
  }

  /** Get current count within window (for monitoring) */
  getCurrentCount(): number {
    const now = Date.now();
    this.timestamps = this.timestamps.filter((t) => now - t < this.windowMs);
    return this.timestamps.length;
  }

  /** Reset the rate limiter (for testing) */
  reset(): void {
    this.timestamps = [];
  }
}

/**
 * Tracks filtered error volume for monitoring.
 * Helps validate filtering patterns without over-filtering.
 */
class FilteredErrorMonitor {
  private filteredCount = 0;
  private sentCount = 0;
  private lastReportTime = Date.now();
  private readonly reportIntervalMs = 5 * 60 * 1000; // 5 minutes

  recordFiltered(): void {
    this.filteredCount++;
  }

  recordSent(): void {
    this.sentCount++;
  }

  /**
   * Get stats and optionally report if interval has passed.
   * Call this periodically to monitor filtering effectiveness.
   */
  getStats(): { filtered: number; sent: number; ratio: string } {
    const total = this.filteredCount + this.sentCount;
    const ratio =
      total > 0 ? `${Math.round((this.filteredCount / total) * 100)}%` : "N/A";
    return {
      filtered: this.filteredCount,
      sent: this.sentCount,
      ratio,
    };
  }

  /**
   * Check if it's time to report stats (called internally).
   * Reports as Sentry breadcrumb for visibility.
   * Only reports if there's significant error activity to avoid cluttering breadcrumbs.
   */
  maybeReport(): void {
    const now = Date.now();
    if (now - this.lastReportTime < this.reportIntervalMs) {
      return;
    }

    const stats = this.getStats();

    // Only report if there's significant filtering activity
    const totalErrors = stats.filtered + stats.sent;
    const shouldReport = totalErrors > 10; // Threshold: report if >10 errors in 5 min

    if (shouldReport && stats.filtered > 0) {
      Sentry.addBreadcrumb({
        message: `Error filtering stats: ${stats.filtered} filtered, ${stats.sent} sent (${stats.ratio} filtered)`,
        level: "info",
        category: "logger_stats",
        data: stats,
      });
    }

    // Reset counters
    this.filteredCount = 0;
    this.sentCount = 0;
    this.lastReportTime = now;
  }

  /** Reset the monitor (for testing) */
  reset(): void {
    this.filteredCount = 0;
    this.sentCount = 0;
    this.lastReportTime = Date.now();
  }
}

class Logger {
  private isDevelopment = process.env.NODE_ENV !== "production";
  private isProduction = process.env.NODE_ENV === "production";
  private isTest = process.env.NODE_ENV === "test";

  // Rate limiter for filtered error breadcrumbs
  private breadcrumbLimiter = new BreadcrumbRateLimiter();

  // Monitor for filtered error volume
  private filterMonitor = new FilteredErrorMonitor();

  // Debug configuration
  private debugConfig = {
    general: process.env.NODE_ENV === "test",
  };

  /**
   * Determines if an error should be sent to Sentry or filtered out.
   * Uses shared constants from ./constants.ts for consistency.
   *
   * @param error - The actual error object (or unknown)
   * @returns true if error should be sent to Sentry, false to filter it out
   */
  private shouldSendToSentry(error: unknown): boolean {
    // No error provided - nothing to filter
    if (!error) {
      return true;
    }

    if (error instanceof Error) {
      // 1. Filter by error class name (ZodError, ValidationError)
      if (isFilteredErrorName(error.name)) {
        return false;
      }

      // 2. Filter by fieldErrors property (validation errors)
      if ("fieldErrors" in error && Array.isArray(error.fieldErrors)) {
        return false;
      }

      // 3. Filter by message patterns (shared with beforeSend hook)
      if (isFilteredErrorMessage(error.message)) {
        return false;
      }
    }

    // 4. Send everything else to Sentry
    return true;
  }

  /**
   * Get error category using shared categorization logic.
   */
  private getErrorCategory(error: Error): ErrorCategory {
    return categorizeError(error);
  }

  /**
   * Get current filtering stats for monitoring.
   * Useful for debugging and validating filter effectiveness.
   */
  getFilteringStats(): FilteringStats {
    return this.filterMonitor.getStats();
  }

  /**
   * Reset rate limiters and monitors (for testing only).
   * Call this in beforeEach() to ensure clean state between tests.
   */
  _resetForTesting(): void {
    this.breadcrumbLimiter.reset();
    this.filterMonitor.reset();
  }

  private log(level: LogLevel, message: string, context?: LogContext): void {
    // In development, always console log
    if (this.isDevelopment) {
      const consoleLevel = level === "fatal" ? "error" : level;
      if (consoleLevel === "error" || consoleLevel === "trace") {
        console.error(message, context);
      } else {
        console[consoleLevel](message, context);
      }
    }

    // Send to Sentry based on level and environment
    const safeContext =
      context && typeof context === "object"
        ? (context as Record<string, unknown>)
        : { data: context };

    // Handle warnings
    if (level === "warn" && !this.isDevelopment) {
      Sentry.captureMessage(message, {
        level: "warning",
        extra: safeContext,
      });
    } else if (level === "info" && this.isProduction) {
      // Only send info logs to Sentry in production if they're important
      Sentry.addBreadcrumb({
        message,
        level: "info",
        data: safeContext,
      });
    }
  }

  /**
   * Lowest level logging for very detailed tracing.
   * Only logs locally in development.
   */
  trace(message: string, context?: LogContext): void {
    if (this.isDevelopment && !this.debugConfig.general) {
      return;
    }

    const safeContext =
      context && typeof context === "object"
        ? (context as Record<string, unknown>)
        : { data: context };

    if (this.isDevelopment) {
      console.log(`[TRACE] ${message}`, safeContext);
    }
  }

  debug(message: string, context?: LogContext): void {
    // Skip if general debug logging is disabled
    if (this.isDevelopment && !this.debugConfig.general) {
      return;
    }

    const safeContext =
      context && typeof context === "object"
        ? (context as Record<string, unknown>)
        : { data: context };

    if (this.isDevelopment) {
      console.log(message, safeContext);
    }
  }

  info(message: string, context?: LogContext): void {
    this.log("info", message, context);
  }

  warn(message: string, context?: LogContext): void {
    this.log("warn", message, context);
  }

  error(message: string, error?: unknown, context?: LogContext): void {
    const safeContext =
      context && typeof context === "object"
        ? (context as Record<string, unknown>)
        : { data: context };

    const errorContext = {
      ...safeContext,
      ...(error instanceof Error
        ? {
            errorMessage: error.message,
            errorStack: error.stack,
            errorName: error.name,
            errorCategory: this.getErrorCategory(error),
          }
        : { error }),
    };

    // Always log locally (console in dev)
    this.log("error", message, errorContext);

    // Send ORIGINAL error to Sentry (preserves stack trace!)
    // Only if it's not a user-facing error
    if (error instanceof Error) {
      if (this.shouldSendToSentry(error)) {
        // Real bug → send to Sentry
        this.filterMonitor.recordSent();
        Sentry.captureException(error, {
          level: "error",
          tags: {
            error_category: this.getErrorCategory(error),
            error_type: error.name,
            error_source: safeContext.component ? "user_facing" : "internal",
          },
          extra: {
            logMessage: message,
            ...safeContext,
          },
        });
      } else {
        // User-facing error → add as breadcrumb for context
        this.filterMonitor.recordFiltered();

        // Rate limit breadcrumbs to prevent flooding
        if (this.breadcrumbLimiter.canAdd()) {
          Sentry.addBreadcrumb({
            message: `[Filtered] ${error.name}: ${error.message}`,
            level: "warning",
            category: "user_error",
            data: {
              errorCategory: this.getErrorCategory(error),
              logMessage: message,
              ...safeContext,
            },
          });
        }

        if (this.isDevelopment) {
          console.warn(
            `[Filtered from Sentry] ${error.name}: ${error.message}`,
          );
        }
      }

      // Periodically report filtering stats
      this.filterMonitor.maybeReport();
    }
  }

  /**
   * Critical errors that should always be reported.
   * Use for unrecoverable errors like app crashes.
   */
  fatal(message: string, error?: unknown, context?: LogContext): void {
    const safeContext =
      context && typeof context === "object"
        ? (context as Record<string, unknown>)
        : { data: context };

    // Always log to console as error
    if (this.isDevelopment) {
      console.error(`[FATAL] ${message}`, error, safeContext);
    }

    // Always capture fatal errors as Sentry Issues
    if (error instanceof Error) {
      Sentry.captureException(error, {
        level: "fatal",
        tags: {
          error_category: this.getErrorCategory(error),
          error_type: error.name,
        },
        extra: {
          logMessage: message,
          ...safeContext,
        },
      });
    } else {
      Sentry.captureMessage(message, {
        level: "fatal",
        extra: { error, ...safeContext },
      });
    }
  }
}

export const logger = new Logger();
